/**
 * LinkedIn Insight Tag Provider
 *
 * Implements LinkedIn advertising pixel for conversion tracking.
 * Ported from deepsource.com composables/adTracking/providers/linkedin.ts
 */

import type { AdTrackingProvider } from '~/types/tracking'

declare global {
  interface Window {
    _linkedin_data_partner_ids?: string[]
    lintrk?: (command: string, data?: Record<string, unknown>) => void
  }
}

const isDev = process.env.NODE_ENV === 'development'

interface LinkedInConfig {
  partnerId: string
}

export const createLinkedInProvider = (config: LinkedInConfig): AdTrackingProvider => {
  let isLoaded = false
  const { partnerId } = config

  const loadScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Window not available'))
        return
      }

      if (window.lintrk) {
        resolve()
        return
      }

      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || []
      window._linkedin_data_partner_ids.push(partnerId)

      const script = document.createElement('script')
      script.async = true
      script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js'

      script.onload = () => {
        setTimeout(() => resolve(), 100)
      }
      script.onerror = () => reject(new Error('Failed to load LinkedIn Insight Tag'))

      document.head.appendChild(script)
    })
  }

  return {
    name: 'linkedin',

    async init() {
      if (!partnerId) {
        if (isDev) {
          console.log('LinkedIn Insight Tag: disabled (no partner ID configured)')
        }
        return
      }

      try {
        await loadScript()
        isLoaded = true

        if (isDev) {
          console.log('LinkedIn Insight Tag initialized:', partnerId)
        }
      } catch (error) {
        if (isDev) {
          console.warn('Failed to initialize LinkedIn Insight Tag:', error)
        }
      }
    },

    trackPageView(_url?: string) {
      if (!isLoaded) return

      if (isDev) {
        console.log('LinkedIn page view (automatic)')
      }
    },

    trackEvent(eventName: string, params?: Record<string, unknown>) {
      if (!isLoaded || !window.lintrk) return

      window.lintrk('track', { conversion_id: eventName, ...params })

      if (isDev) {
        console.log('LinkedIn event tracked:', eventName, params)
      }
    },

    trackConversion(conversionType: string, params?: Record<string, unknown>) {
      if (!isLoaded || !window.lintrk) return

      window.lintrk('track', {
        conversion_id: conversionType,
        ...params
      })

      if (isDev) {
        console.log('LinkedIn conversion tracked:', conversionType, params)
      }
    },

    isAvailable() {
      return Boolean(partnerId && isLoaded)
    },

    cleanup() {
      isLoaded = false
    }
  }
}
